import React, { useState, useEffect } from 'react';
import { DndProvider, useDrag, useDrop } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import AdminSidebar from '../../components/AdminSidebar';
import AdminTopBar from '../../components/AdminTopBar';
import { API_BASE_URL } from '../../config';

const API = API_BASE_URL;

// Draggable Advertisement Card Component
const DraggableAdCard = ({ ad, index, moveAdvertisement, onEdit, onDelete, isActive, formatDate }) => {
  const [{ isDragging }, drag] = useDrag({
    type: 'advertisement',
    item: { index },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  });

  const [, drop] = useDrop({
    accept: 'advertisement',
    hover: (item) => {
      if (item.index !== index) {
        moveAdvertisement(item.index, index);
        item.index = index;
      }
    },
  });

  return (
    <div
      ref={(node) => drag(drop(node))}
      className={`border border-gray-200 dark:border-gray-700 rounded-lg p-4 cursor-move transition-all ${
        isDragging ? 'opacity-50 rotate-2 scale-105' : 'hover:shadow-md'
      }`}
    >
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-2 mb-2">
          <span className="material-symbols-outlined text-gray-400 text-lg cursor-grab active:cursor-grabbing">
            drag_indicator
          </span>
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-2">
              <span className="font-semibold text-lg text-gray-900 dark:text-white">{ad.title}</span>
              <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${
                isActive(ad) 
                  ? 'bg-green-100 text-green-800' 
                  : 'bg-gray-100 text-gray-800'
              }`}>
                {isActive(ad) ? 'Aktif' : 'Pasif'}
              </span>
            </div>
            
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm text-gray-600 dark:text-gray-400">
              <div>
                <span className="font-medium">Süre:</span> {ad.duration / 1000}s
              </div>
              <div>
                <span className="font-medium">Başlangıç:</span> {formatDate(ad.startDate)}
              </div>
              <div>
                <span className="font-medium">Bitiş:</span> {formatDate(ad.endDate)}
              </div>
              <div>
                <span className="font-medium">Sıra:</span> {ad.orderIndex}
              </div>
            </div>
            
            {ad.content && ad.content.description && (
              <p className="mt-2 text-gray-600 dark:text-gray-400 text-sm">{ad.content.description}</p>
            )}
          </div>
        </div>
        
        <div className="flex gap-2">
          <button
            onClick={() => onEdit(ad)}
            className="text-blue-600 hover:text-blue-800 text-sm font-medium"
          >
            Düzenle
          </button>
          <button
            onClick={() => onDelete(ad.id)}
            className="text-red-600 hover:text-red-800 text-sm font-medium"
          >
            Sil
          </button>
        </div>
      </div>
    </div>
  );
};

const AdminAdvertisements = () => {
  const [activeTab, setActiveTab] = useState(1);
  const [advertisements, setAdvertisements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingAd, setEditingAd] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchAdvertisements();
  }, [activeTab]);

  const fetchAdvertisements = async () => {
    try {
      setLoading(true);
      const res = await fetch(`${API}/api/advertisements/admin?area=${activeTab}`, {
        credentials: 'include'
      });
      if (res.ok) {
        const data = await res.json();
        setAdvertisements(data.ads || []);
      }
    } catch (error) {
      console.error('Error fetching advertisements:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (ad) => {
    setEditingAd(ad);
    setShowModal(true);
  };

  const handleCreate = () => {
    setEditingAd(null);
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Bu reklamı silmek istediğinizden emin misiniz?')) return;
    
    try {
      const res = await fetch(`${API}/api/advertisements/${id}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      if (res.ok) {
        fetchAdvertisements();
      }
    } catch (error) {
      console.error('Error deleting advertisement:', error);
    }
  };

  const handleSave = async (adData) => {
    try {
      setSaving(true);
      setSaveSuccess(false);
      
      // Tarihleri ISO formatına çevir
      const processedData = {
        ...adData,
        startDate: adData.startDate ? new Date(adData.startDate).toISOString() : new Date().toISOString(),
        endDate: adData.endDate ? new Date(adData.endDate).toISOString() : null
      };
      
      console.log('📤 Processed data:', processedData);
      
      const method = editingAd ? 'PUT' : 'POST';
      const url = editingAd ? `${API}/api/advertisements/${editingAd.id}` : `${API}/api/advertisements`;
      
      const res = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(processedData)
      });

      if (res.ok) {
        setSaveSuccess(true);
        setTimeout(() => {
          setShowModal(false);
          setEditingAd(null);
          setSaveSuccess(false);
          fetchAdvertisements();
        }, 1500);
      } else {
        throw new Error(`HTTP error! status: ${res.status}`);
      }
    } catch (error) {
      console.error('Error saving advertisement:', error);
      alert('Kayıt sırasında bir hata oluştu. Lütfen tekrar deneyin.');
    } finally {
      setSaving(false);
    }
  };

  const updateOrder = async (updates) => {
    try {
      const res = await fetch(`${API}/api/advertisements/reorder`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ updates })
      });

      if (!res.ok) {
        throw new Error(`HTTP error! status: ${res.status}`);
      }
      
      console.log('✅ Order updated successfully');
    } catch (error) {
      console.error('Error updating order:', error);
      alert('Sıralama güncellenirken hata oluştu.');
    }
  };

  const moveAdvertisement = (dragIndex, hoverIndex) => {
    // Get current area ads sorted by orderIndex
    const currentAreaAds = advertisements
      .filter(ad => ad.area === activeTab)
      .sort((a, b) => a.orderIndex - b.orderIndex);
    
    if (dragIndex >= currentAreaAds.length || hoverIndex >= currentAreaAds.length) {
      console.error('Invalid drag/hover index:', { dragIndex, hoverIndex, totalAds: currentAreaAds.length });
      return;
    }
    
    const draggedItem = currentAreaAds[dragIndex];
    console.log('🔄 Moving advertisement:', { 
      draggedItem: draggedItem.title, 
      from: dragIndex, 
      to: hoverIndex 
    });
    
    // Create new order
    const newOrder = [...currentAreaAds];
    newOrder.splice(dragIndex, 1);
    newOrder.splice(hoverIndex, 0, draggedItem);
    
    // Update local state immediately for smooth UI
    const updatedAds = advertisements.map(ad => {
      if (ad.area !== activeTab) return ad;
      const newIndex = newOrder.findIndex(item => item.id === ad.id);
      return newIndex !== -1 ? { ...ad, orderIndex: newIndex } : ad;
    });
    
    setAdvertisements(updatedAds);
    
    // Prepare backend update
    const updates = newOrder.map((ad, index) => ({
      id: ad.id,
      orderIndex: index
    }));
    
    console.log('📤 Sending reorder request:', updates);
    updateOrder(updates);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Süresiz';
    return new Date(dateString).toLocaleDateString('tr-TR');
  };

  const isActive = (ad) => {
    const now = new Date();
    const startDate = new Date(ad.startDate);
    const endDate = ad.endDate ? new Date(ad.endDate) : null;
    
    return ad.active && startDate <= now && (!endDate || endDate >= now);
  };

  return (
    <DndProvider backend={HTML5Backend}>
      <div className="flex h-screen bg-gray-100 dark:bg-gray-900">
      {/* Admin Sidebar */}
      <AdminSidebar mobileOpen={sidebarOpen} onCloseMobile={() => setSidebarOpen(false)} />
      
      {/* Main Content Area */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Admin TopBar */}
        <AdminTopBar title="Reklam Yönetimi" onToggleMobile={() => setSidebarOpen(!sidebarOpen)} />
        
        {/* Content */}
        <main className="flex-1 overflow-y-auto bg-gray-50 dark:bg-gray-900">
          <div className="p-6">
            <div className="max-w-6xl mx-auto">
              <div className="mb-6">
                <p className="text-gray-600 dark:text-gray-400">Ana sayfada gösterilecek reklamları yönetin.</p>
              </div>

              {/* Tab Navigation */}
              <div className="mb-6">
                <div className="border-b border-gray-200 dark:border-gray-700">
                  <nav className="flex space-x-8">
                    {[1, 2, 3].map((area) => (
                      <button
                        key={area}
                        onClick={() => setActiveTab(area)}
                        className={`py-2 px-4 border-b-2 font-medium text-sm transition-colors ${
                          activeTab === area
                            ? 'border-primary text-primary'
                            : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600'
                        }`}
                      >
                  Reklam Alan {area} {area === 1 ? 'aktif' : ''}
                </button>
              ))}
            </nav>
          </div>
        </div>

              {/* Add New Ad Button */}
              <div className="mb-6">
                <button
                  onClick={handleCreate}
                  className="bg-primary hover:bg-red-600 text-white px-4 py-2 rounded-lg transition-colors"
                >
                  + Yeni Reklam Ekle
                </button>
              </div>

              {/* Advertisements List */}
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
                {loading ? (
                  <div className="p-8 text-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
                    <p className="mt-2 text-gray-600 dark:text-gray-400">Yüklüniyor...</p>
                  </div>
                ) : advertisements.length === 0 ? (
                  <div className="p-8 text-center">
                    <p className="text-gray-600 dark:text-gray-400">Bu alanda henüz reklam bulunmuyor.</p>
                  </div>
          ) : (
            <div className="space-y-4 p-6">
                {advertisements
                  .filter(ad => ad.area === activeTab)
                  .sort((a, b) => a.orderIndex - b.orderIndex)
                  .map((ad, index) => (
                  <DraggableAdCard 
                    key={ad.id} 
                    ad={ad} 
                    index={index}
                    moveAdvertisement={moveAdvertisement}
                    onEdit={handleEdit}
                    onDelete={handleDelete}
                    isActive={isActive}
                    formatDate={formatDate}
                  />
                ))}
            </div>
          )}
        </div>

              {/* Modal */}
              {showModal && (
                <AdModal
                  ad={editingAd}
                  area={activeTab}
                  onSave={handleSave}
                  onClose={() => {
                    setShowModal(false);
                    setEditingAd(null);
                    setSaveSuccess(false);
                  }}
                  saving={saving}
                  saveSuccess={saveSuccess}
                />
              )}
            </div>
          </div>
        </main>
      </div>
    </div>
    </DndProvider>
  );
};

const AdModal = ({ ad, area, onSave, onClose, saving = false, saveSuccess = false }) => {
  const [formData, setFormData] = useState({
    title: ad?.title || '',
    area: ad?.area || area,
    duration: ad?.duration || 5000,
    startDate: ad?.startDate ? ad.startDate.split('T')[0] : new Date().toISOString().split('T')[0],
    endDate: ad?.endDate ? ad.endDate.split('T')[0] : '',
    active: ad?.active !== undefined ? ad.active : true,
    orderIndex: ad?.orderIndex || 0,
    content: {
      title: ad?.content?.title || '',
      subtitle: ad?.content?.subtitle || '',
      description: ad?.content?.description || '',
      image: ad?.content?.image || '',
      buttonText: ad?.content?.buttonText || 'Şimdi Keşfet',
      buttonLink: ad?.content?.buttonLink || '',
      backgroundColor: ad?.content?.backgroundColor || '#ffffff',
      textColor: ad?.content?.textColor || '#000000'
    }
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Validation: title alanının dolu olduğundan emin ol
    if (!formData.title.trim()) {
      alert('Reklam başlığı zorunludur!');
      return;
    }
    
    console.log('📤 Saving advertisement data:', formData);
    onSave(formData);
  };

  const handleContentChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      content: { ...prev.content, [field]: value }
    }));
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg max-w-4xl w-full max-h-screen overflow-y-auto">
        <div className="p-6 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center">
          <h2 className="text-xl font-bold text-gray-900 dark:text-white">
            {ad ? 'Reklamı Düzenle' : 'Yeni Reklam Oluştur'}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300 transition-colors"
          >
            <span className="material-symbols-outlined text-2xl">close</span>
          </button>
        </div>
        
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Basic Info */}
            <div className="space-y-4">
              <h3 className="font-semibold text-lg text-gray-900 dark:text-white">Temel Bilgiler</h3>
              
              <div>
                <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-gray-300">Başlık</label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                  className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-gray-300">Gösterim Süresi (ms)</label>
                <input
                  type="number"
                  value={formData.duration}
                  onChange={(e) => setFormData(prev => ({ ...prev, duration: parseInt(e.target.value) }))}
                  className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  min="1000"
                  step="1000"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-gray-300">Başlangıç Tarihi</label>
                <input
                  type="date"
                  value={formData.startDate}
                  onChange={(e) => setFormData(prev => ({ ...prev, startDate: e.target.value }))}
                  className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-gray-300">Bitiş Tarihi (İsteğe bağlı)</label>
                <input
                  type="date"
                  value={formData.endDate}
                  onChange={(e) => setFormData(prev => ({ ...prev, endDate: e.target.value }))}
                  className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-gray-300">Sıra</label>
                <input
                  type="number"
                  value={formData.orderIndex}
                  onChange={(e) => setFormData(prev => ({ ...prev, orderIndex: parseInt(e.target.value) }))}
                  className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  min="0"
                />
              </div>

              <div>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.active}
                    onChange={(e) => setFormData(prev => ({ ...prev, active: e.target.checked }))}
                    className="mr-2"
                  />
                  Aktif
                </label>
              </div>
            </div>

            {/* Content */}
            <div className="space-y-4">
              <h3 className="font-semibold text-lg text-gray-900 dark:text-white">İçerik</h3>
              
              <div>
                <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-gray-300">Reklam Başlığı</label>
                <input
                  type="text"
                  value={formData.content.title}
                  onChange={(e) => handleContentChange('title', e.target.value)}
                  className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  placeholder="Şefin Özel Bıçak Seti"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-gray-300">Alt Başlık</label>
                <input
                  type="text"
                  value={formData.content.subtitle}
                  onChange={(e) => handleContentChange('subtitle', e.target.value)}
                  className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  placeholder="YENİ ÜRÜN"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-gray-300">Açıklama</label>
                <textarea
                  value={formData.content.description}
                  onChange={(e) => handleContentChange('description', e.target.value)}
                  className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 h-24 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  placeholder="Profesyonel mutfak deneyimini evinize taşıyın..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-gray-300">Resim URL</label>
                <input
                  type="url"
                  value={formData.content.image}
                  onChange={(e) => handleContentChange('image', e.target.value)}
                  className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  placeholder="https://example.com/image.jpg"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-gray-300">Buton Metni</label>
                <input
                  type="text"
                  value={formData.content.buttonText}
                  onChange={(e) => handleContentChange('buttonText', e.target.value)}
                  className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-gray-300">Buton Linki</label>
                <input
                  type="url"
                  value={formData.content.buttonLink}
                  onChange={(e) => handleContentChange('buttonLink', e.target.value)}
                  className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  placeholder="https://example.com"
                />
              </div>
            </div>
          </div>

          {/* Preview */}
          <div className="border-t border-gray-200 dark:border-gray-700 pt-6">
            <h3 className="font-semibold text-lg mb-4 text-gray-900 dark:text-white">Önizleme</h3>
            <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 bg-gray-50 dark:bg-gray-700">
              <div className="flex flex-col md:flex-row bg-white rounded-lg overflow-hidden shadow-sm">
                {formData.content.image && (
                  <div className="h-48 w-full md:h-auto md:w-2/5 bg-cover bg-center"
                       style={{ backgroundImage: `url('${formData.content.image}')` }}>
                  </div>
                )}
                <div className="flex flex-1 flex-col justify-center p-6">
                  {formData.content.subtitle && (
                    <span className="inline-flex items-center rounded-full bg-yellow-100 px-2.5 py-0.5 text-xs font-bold text-yellow-800 mb-2 w-fit">
                      {formData.content.subtitle}
                    </span>
                  )}
                  {formData.content.title && (
                    <h3 className="text-xl font-bold text-gray-900 mb-3">{formData.content.title}</h3>
                  )}
                  {formData.content.description && (
                    <p className="text-gray-500 mb-4">{formData.content.description}</p>
                  )}
                  {formData.content.buttonText && (
                    <button className="w-fit rounded-xl bg-primary px-6 py-3 text-sm font-semibold text-white">
                      {formData.content.buttonText}
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-4 border-t border-gray-200 dark:border-gray-700 pt-6">
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
            >
              İptal
            </button>
            <button
              type="submit"
              disabled={saving}
              className={`px-6 py-2 rounded-lg text-white font-medium transition-colors flex items-center gap-2 ${
                saveSuccess 
                  ? 'bg-green-600 hover:bg-green-700' 
                  : saving 
                    ? 'bg-gray-400 cursor-not-allowed' 
                    : 'bg-primary hover:bg-red-600'
              }`}
            >
              {saving ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  Kaydediliyor...
                </>
              ) : saveSuccess ? (
                <>
                  <span className="material-symbols-outlined text-lg">check_circle</span>
                  Kayıt Edildi!
                </>
              ) : (
                'Kaydet'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AdminAdvertisements;
